/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Small dashboard card that nudges a professional to complete their Stripe
 * Connect KYC before they can receive payouts. Renders nothing when the
 * account is already fully verified so it stays out of the way.
 *
 * Props
 *   embedded    when true, uses compact styling (for command centers)
 */
export const KycStripeCard = ({ embedded = true }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/telemedicine/doctor/me');
      setProfile(r.data);
    } catch { setProfile(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const r = await api.post('/telemedicine/doctor/connect/onboard', {});
      const url = r.data?.url || r.data?.account_link_url;
      if (url) {
        window.open(url, '_blank', 'noopener');
        toast.success('Apri la scheda Stripe per completare la verifica');
      } else {
        toast.error('Link Stripe non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore avvio KYC Stripe');
    } finally { setStarting(false); }
  };

  if (loading || !profile) return null;

  // Already fully ready → don't clutter the dashboard
  const chargesOk = !!profile.stripe_connect_charges_enabled;
  const payoutsOk = !!profile.stripe_connect_payouts_enabled;
  const submitted = !!profile.stripe_connect_details_submitted;
  const hasAccount = !!profile.stripe_connect_account_id;
  const ready = chargesOk && payoutsOk;

  if (ready && embedded) {
    // Show a slim green confirmation strip
    return (
      <div className="flex items-center gap-2 text-[11px] rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-2.5 py-1.5" data-testid="kyc-stripe-ok">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Stripe Connect: pagamenti e payout attivi
      </div>
    );
  }

  const dueCount = (profile.stripe_connect_requirements_due || []).length;

  return (
    <Card className="border-amber-500/40 bg-amber-500/5" data-testid="kyc-stripe-card">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-full flex items-center justify-center bg-amber-500/20 text-amber-700 dark:text-amber-300">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-sm">Completa verifica Stripe</div>
              {!hasAccount && <Badge variant="outline" className="text-[10px]">non avviato</Badge>}
              {hasAccount && !submitted && <Badge variant="outline" className="text-[10px]">in compilazione</Badge>}
              {submitted && !ready && <Badge variant="outline" className="text-[10px]">in verifica</Badge>}
              {dueCount > 0 && <Badge variant="destructive" className="text-[10px]">{dueCount} document{dueCount === 1 ? 'o' : 'i'} mancant{dueCount === 1 ? 'e' : 'i'}</Badge>}
            </div>
            <p className="text-[11.5px] text-muted-foreground mt-0.5">
              Verifica KYC richiesta per ricevere i pagamenti delle visite. Il flusso apre Stripe Express in una nuova scheda.
            </p>
            {(profile.stripe_connect_requirements_due || []).length > 0 && (
              <div className="mt-1 flex items-start gap-1.5 text-[10.5px] text-amber-800 dark:text-amber-200">
                <AlertTriangle className="h-3 w-3 mt-0.5" />
                <span className="font-mono">{profile.stripe_connect_requirements_due.slice(0, 3).join(', ')}
                  {profile.stripe_connect_requirements_due.length > 3 && '…'}
                </span>
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading} className="h-7 w-7" data-testid="kyc-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" onClick={startOnboarding} disabled={starting} data-testid="kyc-start">
            <KeyRound className="h-3.5 w-3.5 mr-1" />
            {hasAccount ? 'Riprendi verifica' : 'Avvia verifica'}
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default KycStripeCard;
